// =============================================================================
//  Server Monitor — live status of the host running Alpaca Intelligence.
//  Polls /api/v1/server-monitor/status every 30s.  Matches the v5 mockup:
//    · Header with server name, IP, alert config
//    · Warning banner (auto-shown when any metric crosses threshold)
//    · 4 metric cards: CPU donut, Memory donut, Services list, Network metrics
//    · Disk usage bars (3 disks)
//    · Recent log entries table with All/Info/Warn/Error tabs
// =============================================================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import './servermonitor.css';
import '../backlog/backlog.css';   // shared .sa-filter-btn chips (BO UI Standard)
import { LoaderSymbol } from '../../components/loading';
import { FormTitle } from '../../components/FormTitle';
import { fmtLocal, fmtLocalTime } from '../../utils/datetime';

// ── Types (match ServerStatusDto on the API side) ─────────────────────────
interface ServerStatusDto {
  lastUpdated: string;
  serverName: string;
  ipAddress: string;
  uptimeDays: number;
  allSystemsOk: boolean;
  warnings: string[];
  cpu: {
    usagePercent: number;
    cores: number;
    topConsumers: { name: string; category: string; percent: number; processCount: number; appPool: string | null }[];
  };
  memory: {
    usedGb: number;
    totalGb: number;
    usagePercent: number;
    // 2026-06-09 — per-process breakdown so the Server Monitor can answer
    // "which app ate the 96% RAM?".  Empty array → panel hides.
    topConsumers?: {
      name: string; category: string; memoryMb: number;
      percentOfTotal: number; processCount: number; appPool: string | null;
    }[];
  };
  services: { name: string; state: string; online: boolean }[];
  disks: { letter: string; label: string; usedGb: number; totalGb: number; usagePercent: number }[];
  network: { responseTimeMs: number; activeSessions: number; requestsPerMinute: number; uptimeDays: number };
  recentLogs: { time: string; level: string; source: string; message: string }[];
  alerts: { warningEmail: string; criticalEmail: string };
  // 2026-06-09 — IIS app-pool memory breakdown.  One row per w3wp.exe
  // process found on the host (highest RAM first).  Empty list when IIS
  // isn't running or the host isn't Windows; in that case the panel
  // hides itself instead of showing a stub.
  iisAppPools?: {
    appPool: string;
    pid: number;
    memoryMb: number;
    percentOfTotal: number;
    cpuPercent: number;
  }[];
  // 2026-06-09 — XAF / .NET Framework runtime deep-dive.  One row per
  // w3wp.exe with LOH, Gen 2 GC, threads, handles, req/s, err/s.  Helps
  // diagnose ALPACA memory leaks without touching XAF code.
  xafRuntimes?: {
    appPool: string;
    pid: number;
    workingSetMb: number;
    privateMb: number;
    lohSizeMb: number;
    heapSizeMb: number;
    gen0Collections: number;
    gen1Collections: number;
    gen2Collections: number;
    threadCount: number;
    handleCount: number;
    requestsPerSec: number;
    errorsPerSec: number;
    sessionsActive: number;
  }[];
}

type LogFilter = 'all' | 'info' | 'warn' | 'error';

// ── Helpers ─────────────────────────────────────────────────────────────
// Render every timestamp through the shared datetime helpers (§14b.6 — local
// timezone, 24-hour, no AM/PM, force-UTC enforced inside the helper).  Never
// roll a local formatter.
const fmtTime = (iso: string) => fmtLocal(iso);            // MM/DD/YYYY HH:mm
const fmtTimeShort = (iso: string) => fmtLocalTime(iso);   // HH:mm only

// ── Donut chart (inline SVG, no chart libs) ─────────────────────────────
function Donut({ pct, color, size = 140 }: { pct: number; color: string; size?: number }) {
  const r = size / 2 - 14;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  const cx = size / 2, cy = size / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} stroke="#eef2f6" strokeWidth="12" fill="none" />
      <circle cx={cx} cy={cy} r={r} stroke={color} strokeWidth="12" fill="none"
              strokeDasharray={`${dash} ${c - dash}`}
              strokeDashoffset={c * 0.25}
              transform={`rotate(-90 ${cx} ${cy})`}
              strokeLinecap="round" />
    </svg>
  );
}

// ── CPU Breakdown ────────────────────────────────────────────────────────
// Horizontal stacked bar + legend showing who is using the CPU right now.
// Color-coded by category so IIS / SQL / Alpaca / Other are easy to spot.
const categoryColor: Record<string, string> = {
  IIS:    '#2563eb',  // blue
  SQL:    '#dc2626',  // red
  Alpaca: '#0d9488',  // teal
  Other:  '#6b7280',  // gray
};
type CpuConsumer = { name: string; category: string; percent: number; processCount: number; appPool: string | null };
function CpuBreakdownCard({ consumers, totalPct }: { consumers: CpuConsumer[]; totalPct: number }) {
  const rows = consumers ?? [];
  // Compute "Idle/free" so the bar sums to 100
  const sumActive = rows.reduce((a, r) => a + r.percent, 0);
  const idlePct = Math.max(0, 100 - sumActive);
  return (
    <div style={{
      background: 'var(--ai-card)', border: '1px solid var(--ai-line)', borderRadius: 10,
      boxShadow: 'var(--ai-shadow-card)',
      padding: '14px 20px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          <i className="ti ti-chart-bar" style={{ marginRight: 6 }} />
          CPU Breakdown
          <span style={{ marginLeft: 8, color: 'var(--ai-muted)', fontWeight: 400, fontSize: 11 }}>
            who is using the CPU
          </span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--ai-muted)' }}>
          Total active: <b>{Math.round(totalPct)}%</b>
        </div>
      </div>

      {rows.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--ai-muted)', padding: '8px 0' }}>
          No active processes captured in the last sample window.
        </div>
      ) : (
        <>
          {/* Stacked bar */}
          <div style={{ display: 'flex', height: 14, borderRadius: 4, overflow: 'hidden',
                        background: '#eef2f6', marginBottom: 10 }}>
            {rows.map((r, i) => (
              <div key={i} title={`${r.name} — ${r.percent}%`}
                   style={{
                     width: `${r.percent}%`,
                     background: categoryColor[r.category] ?? categoryColor.Other,
                     transition: 'width 0.3s',
                     borderRight: i < rows.length - 1 ? '1px solid #fff' : 'none',
                   }} />
            ))}
            <div title={`Idle/free — ${idlePct.toFixed(1)}%`}
                 style={{ flex: 1, background: '#eef2f6' }} />
          </div>

          {/* Legend / table */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', rowGap: 4,
                        fontSize: 12 }}>
            <div style={{ fontWeight: 600, color: '#555' }}>Process / App pool</div>
            <div style={{ fontWeight: 600, color: '#555', textAlign: 'right' }}>CPU %</div>
            <div style={{ fontWeight: 600, color: '#555', textAlign: 'right' }}>Procs</div>
            {rows.map((r, i) => {
              const c = categoryColor[r.category] ?? categoryColor.Other;
              return (
                <React.Fragment key={i}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 2 }}>
                    <span style={{ width: 10, height: 10, background: c, borderRadius: 2,
                                   display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.name}
                    </span>
                    <span style={{ fontSize: 10, color: 'var(--ai-muted)', background: '#f3f4f6',
                                   padding: '1px 6px', borderRadius: 8 }}>
                      {r.category}
                    </span>
                  </div>
                  <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                    {r.percent.toFixed(1)}%
                  </div>
                  <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ai-muted)' }}>
                    {r.processCount}
                  </div>
                </React.Fragment>
              );
            })}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 2 }}>
              <span style={{ width: 10, height: 10, background: '#eef2f6', borderRadius: 2,
                             border: '1px solid #d1d5db', display: 'inline-block' }} />
              <span style={{ color: 'var(--ai-muted)' }}>Idle / free</span>
            </div>
            <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ai-muted)' }}>
              {idlePct.toFixed(1)}%
            </div>
            <div />
          </div>
        </>
      )}
    </div>
  );
}

// Level badge component for log entries
function LevelBadge({ level }: { level: string }) {
  const upper = level.toUpperCase();
  const styles: Record<string, { bg: string; fg: string }> = {
    'INFO':  { bg: '#dbeafe', fg: '#1e40af' },
    'WARN':  { bg: '#fef3c7', fg: '#92400e' },
    'ERROR': { bg: '#fee2e2', fg: '#991b1b' },
  };
  const s = styles[upper] ?? styles['INFO']!;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 3,
      background: s.bg, color: s.fg, fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
      minWidth: 50, textAlign: 'center',
    }}>{upper}</span>
  );
}

// ── Memory Breakdown ─────────────────────────────────────────────────────
// Mirror image of CpuBreakdownCard but the metric is working-set RAM.
// Answers "which app is eating 30 GB of 32 GB?" — broken down by the same
// category labels (IIS app pool / SQL / Alpaca / Other) so the user can
// see at a glance whether the spike is IIS, SQL, or something else.
type MemConsumer = {
  name: string; category: string; memoryMb: number;
  percentOfTotal: number; processCount: number; appPool: string | null;
};
function MemoryBreakdownCard({ consumers, totalGb, usedGb }: {
  consumers: MemConsumer[]; totalGb: number; usedGb: number;
}) {
  const rows = consumers ?? [];
  if (rows.length === 0) return null;
  // Sum what we captured.  "Other (system)" = total used minus what we
  // attributed to named processes, so the user can see how much is OS /
  // kernel / non-process memory and the bar still sums to 100% of used.
  const capturedMb = rows.reduce((a, r) => a + r.memoryMb, 0);
  const usedMb     = usedGb * 1024;
  const otherMb    = Math.max(0, usedMb - capturedMb);
  const otherPct   = totalGb <= 0 ? 0 : Math.round((otherMb / 1024 / totalGb) * 1000) / 10;

  const fmt = (mb: number) => mb >= 1024 ? (mb / 1024).toFixed(2) + ' GB' : mb.toFixed(0) + ' MB';

  return (
    <div style={{
      background: 'var(--ai-card)', border: '1px solid var(--ai-line)', borderRadius: 10,
      boxShadow: 'var(--ai-shadow-card)',
      padding: '14px 20px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          <i className="ti ti-cpu-2" style={{ marginRight: 6, color: '#ea580c' }} />
          Memory Breakdown <span style={{ color: 'var(--ai-muted)', fontWeight: 500 }}>
            — who's using the RAM?
          </span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--ai-muted)' }}>
          {usedGb} / {totalGb} GB used
        </div>
      </div>
      {/* Table chrome (header bg / paddings / separators / ink) comes from the
          shared token-based .smon-tbl classes in servermonitor.css (§18.6). */}
      <table className="smon-tbl">
        <thead>
          <tr>
            <th>Process / App Pool</th>
            <th style={{ width: 90 }}>Category</th>
            <th className="smon-num" style={{ width: 110 }}>Memory</th>
            <th>% of host RAM</th>
            <th className="smon-num" style={{ width: 60 }}>#</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const isTop = i === 0;
            const barColor =
              r.percentOfTotal >= 25 ? '#dc2626' :
              r.percentOfTotal >= 10 ? '#ea580c' :
              r.percentOfTotal >=  5 ? '#d97706' :
                                       categoryColor[r.category] ?? '#6b7280';
            const widthPct = Math.max(2, Math.min(100, r.percentOfTotal * 4));   // 25% = full bar
            return (
              <tr key={r.name}>
                <td style={{ fontWeight: isTop ? 700 : 500 }}>
                  {isTop && <i className="ti ti-trophy"
                              style={{ color: '#d97706', marginRight: 6 }} />}
                  {r.name}
                </td>
                <td>
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 3,
                    background: (categoryColor[r.category] ?? '#6b7280') + '22',
                    color:      categoryColor[r.category] ?? '#374151',
                    fontWeight: 600, fontSize: 11,
                  }}>{r.category}</span>
                </td>
                <td className="smon-num" style={{ fontWeight: 600 }}>
                  {fmt(r.memoryMb)}
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, height: 8, background: '#eef2f6',
                                  borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: widthPct + '%', height: '100%',
                                    background: barColor,
                                    transition: 'width .3s ease-out' }} />
                    </div>
                    <span style={{ width: 50, textAlign: 'right',
                                   fontVariantNumeric: 'tabular-nums',
                                   color: barColor, fontWeight: 600 }}>
                      {r.percentOfTotal.toFixed(1)}%
                    </span>
                  </div>
                </td>
                <td className="smon-num" style={{ color: 'var(--ai-muted)' }}>
                  {r.processCount}
                </td>
              </tr>
            );
          })}
          {otherMb > 50 && (
            <tr>
              <td style={{ color: '#555', fontStyle: 'italic' }}>
                Other (OS / kernel / unattributed)
              </td>
              <td>
                <span style={{
                  display: 'inline-block', padding: '2px 8px', borderRadius: 3,
                  background: '#6b728022', color: '#6b7280',
                  fontWeight: 600, fontSize: 11,
                }}>System</span>
              </td>
              <td className="smon-num" style={{ color: '#555' }}>
                {fmt(otherMb)}
              </td>
              <td style={{ color: 'var(--ai-muted)', fontVariantNumeric: 'tabular-nums' }}>
                ≈ {otherPct.toFixed(1)}%
              </td>
              <td className="smon-num" style={{ color: 'var(--ai-muted)' }}>—</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── ALPACA XAF Runtime ───────────────────────────────────────────────────
// Deep-dive per-w3wp metrics for diagnosing XAF / .NET Framework memory
// issues.  Highlights cells in colour when they cross known-bad thresholds
// so a problem leaps off the screen:
//   - LOH > 500 MB    — large-object heap leak suspect
//   - Gen 2 > 100/min — full-GC churn (memory pressure)
//   - Threads > 500   — request queue building up
//   - Handles > 5000  — handle leak
//   - Errors/sec > 1  — bad requests in flight
type XafRuntime = {
  appPool: string; pid: number;
  workingSetMb: number; privateMb: number;
  lohSizeMb: number; heapSizeMb: number;
  gen0Collections: number; gen1Collections: number; gen2Collections: number;
  threadCount: number; handleCount: number;
  requestsPerSec: number; errorsPerSec: number; sessionsActive: number;
};
function XafRuntimeCard({ rows }: { rows: XafRuntime[] }) {
  if (!rows || rows.length === 0) return null;
  const warn = (cond: boolean, normal: string = '#222') =>
    cond ? '#dc2626' : normal;
  const fmt = (mb: number) => mb >= 1024 ? (mb / 1024).toFixed(2) + ' GB'
                                          : mb.toFixed(0) + ' MB';
  return (
    <div style={{
      background: 'var(--ai-card)', border: '1px solid var(--ai-line)', borderRadius: 10,
      boxShadow: 'var(--ai-shadow-card)',
      padding: '14px 20px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          <i className="ti ti-microscope" style={{ marginRight: 6, color: '#7c3aed' }} />
          ALPACA XAF Runtime <span style={{ color: 'var(--ai-muted)', fontWeight: 500 }}>
            — .NET CLR + ASP.NET deep-dive per app pool
          </span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--ai-muted)' }}>
          Red = exceeds typical healthy threshold
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        {/* Table chrome (header bg / paddings / separators / ink) comes from
            the shared token-based .smon-tbl classes in servermonitor.css. */}
        <table className="smon-tbl">
          <thead>
            <tr>
              <th>App Pool</th>
              <th className="smon-num" title="Process ID">PID</th>
              <th className="smon-num" title="Working Set (Task Manager Memory)">WS</th>
              <th className="smon-num" title="Private bytes (committed memory)">Private</th>
              <th className="smon-num" title="Large Object Heap — classic .NET leak indicator (red if >500 MB)">LOH</th>
              <th className="smon-num" title=".NET managed heap (Gen 0+1+2+LOH)">Heap</th>
              <th className="smon-num" title="Gen 0 / Gen 1 / Gen 2 collections since process start">GC 0/1/2</th>
              <th className="smon-num" title="OS thread count (red if >500)">Thr</th>
              <th className="smon-num" title="OS handle count (red if >5000)">Hnd</th>
              <th className="smon-num" title="ASP.NET requests per second">Req/s</th>
              <th className="smon-num" title="ASP.NET errors per second (red if >1)">Err/s</th>
              <th className="smon-num" title="ASP.NET active sessions">Sess</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.pid}>
                <td style={{ fontWeight: 700 }}>
                  {r.appPool}
                </td>
                <td className="smon-num" style={{ color: 'var(--ai-muted)' }}>{r.pid}</td>
                <td className="smon-num" style={{ fontWeight: 600 }}>{fmt(r.workingSetMb)}</td>
                <td className="smon-num">{fmt(r.privateMb)}</td>
                <td className="smon-num" style={{ color: warn(r.lohSizeMb > 500),
                             fontWeight: r.lohSizeMb > 500 ? 700 : 500 }}>
                  {fmt(r.lohSizeMb)}
                </td>
                <td className="smon-num">{fmt(r.heapSizeMb)}</td>
                <td className="smon-num" style={{ fontSize: 11, color: '#555' }}>
                  {r.gen0Collections.toLocaleString()} /
                  &thinsp;{r.gen1Collections.toLocaleString()} /
                  &thinsp;<span style={{ color: warn(r.gen2Collections > 1000, '#222'),
                                          fontWeight: r.gen2Collections > 1000 ? 700 : 400 }}>
                    {r.gen2Collections.toLocaleString()}
                  </span>
                </td>
                <td className="smon-num" style={{ color: warn(r.threadCount > 500),
                             fontWeight: r.threadCount > 500 ? 700 : 400 }}>
                  {r.threadCount}
                </td>
                <td className="smon-num" style={{ color: warn(r.handleCount > 5000),
                             fontWeight: r.handleCount > 5000 ? 700 : 400 }}>
                  {r.handleCount.toLocaleString()}
                </td>
                <td className="smon-num">{r.requestsPerSec.toFixed(1)}</td>
                <td className="smon-num" style={{ color: warn(r.errorsPerSec > 1),
                             fontWeight: r.errorsPerSec > 1 ? 700 : 400 }}>
                  {r.errorsPerSec.toFixed(2)}
                </td>
                <td className="smon-num" style={{ color: '#444' }}>{r.sessionsActive}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ai-muted)' }}>
        <strong>Reading the table:</strong>&nbsp;
        <b>LOH</b> climbing across a shift = giant DataTables held in static cache (classic XAF leak).&nbsp;
        <b>Threads</b> climbing = stuck requests on SQL/locks.&nbsp;
        <b>Handles</b> climbing = undisposed DB cursors / files.&nbsp;
        <b>Gen 2</b> &gt;1000 in &lt; 24 h = serious memory pressure.
      </div>
    </div>
  );
}

// ── IIS Application Pool Memory ──────────────────────────────────────────
// Shows which IIS app pool is consuming the most RAM on the host.  One
// row per running w3wp.exe.  Empty list → panel hides itself (server
// isn't running IIS, or it's not a Windows host).  Bars are coloured by
// % of total RAM so the top consumer pops orange/red on a hot box.
type IisPool = { appPool: string; pid: number; memoryMb: number; percentOfTotal: number; cpuPercent: number };
function IisAppPoolsCard({ pools, totalGb }: { pools: IisPool[]; totalGb: number }) {
  if (!pools || pools.length === 0) return null;
  // Use the highest single-pool RAM as the bar reference so a small pool
  // doesn't render as an invisible sliver next to a big one.
  const maxMb = Math.max(...pools.map(p => p.memoryMb), 1);
  const totalMb = pools.reduce((a, p) => a + p.memoryMb, 0);
  return (
    <div style={{
      background: 'var(--ai-card)', border: '1px solid var(--ai-line)', borderRadius: 10,
      boxShadow: 'var(--ai-shadow-card)',
      padding: '14px 20px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          <i className="ti ti-server-cog" style={{ marginRight: 6, color: '#2563eb' }} />
          IIS Application Pools — Memory Usage
        </div>
        <div style={{ fontSize: 11, color: 'var(--ai-muted)' }}>
          {pools.length} pool{pools.length === 1 ? '' : 's'} ·
          &nbsp;{(totalMb / 1024).toFixed(2)} GB / {totalGb} GB host RAM
        </div>
      </div>
      {/* Table chrome (header bg / paddings / separators / ink) comes from the
          shared token-based .smon-tbl classes in servermonitor.css (§18.6). */}
      <table className="smon-tbl">
        <thead>
          <tr>
            <th>Application Pool</th>
            <th className="smon-num" style={{ width: 80 }}>PID</th>
            <th className="smon-num" style={{ width: 100 }}>Memory</th>
            <th>% of host RAM</th>
            <th className="smon-num" style={{ width: 80 }}>CPU%</th>
          </tr>
        </thead>
        <tbody>
          {pools.map((p, i) => {
            // Bar scales to the LARGEST pool's MB so the relative ratio
            // is visible at a glance.  Colour escalates with % of host RAM.
            const widthPct = Math.max(2, Math.round((p.memoryMb / maxMb) * 100));
            const barColor =
              p.percentOfTotal >= 25 ? '#dc2626' :   // > 25% host RAM → red
              p.percentOfTotal >= 10 ? '#ea580c' :   // 10-25% → orange
              p.percentOfTotal >=  5 ? '#d97706' :   // 5-10% → amber
                                       '#2563eb';   // <5% → blue
            const isTop = i === 0;
            return (
              <tr key={p.pid}>
                <td style={{ fontWeight: isTop ? 700 : 500 }}>
                  {isTop && <i className="ti ti-trophy"
                              style={{ color: '#d97706', marginRight: 6 }} />}
                  {p.appPool}
                </td>
                <td className="smon-num" style={{ color: 'var(--ai-muted)' }}>
                  {p.pid}
                </td>
                <td className="smon-num" style={{ fontWeight: 600 }}>
                  {p.memoryMb >= 1024
                    ? (p.memoryMb / 1024).toFixed(2) + ' GB'
                    : p.memoryMb.toFixed(0)         + ' MB'}
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, height: 8, background: '#eef2f6',
                                  borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{
                        width: widthPct + '%', height: '100%',
                        background: barColor, transition: 'width .3s ease-out',
                      }} />
                    </div>
                    <span style={{ width: 50, textAlign: 'right',
                                   fontVariantNumeric: 'tabular-nums',
                                   color: barColor, fontWeight: 600 }}>
                      {p.percentOfTotal.toFixed(1)}%
                    </span>
                  </div>
                </td>
                <td className="smon-num" style={{ color: '#444' }}>
                  {p.cpuPercent > 0 ? p.cpuPercent.toFixed(1) + '%' : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function ServerMonitorPage() {
  const [status, setStatus] = useState<ServerStatusDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [logFilter, setLogFilter] = useState<LogFilter>('all');
  const [logSearch, setLogSearch] = useState('');

  const fetchStatus = useCallback(async () => {
    const t0 = performance.now();
    try {
      const r = await api.get<ServerStatusDto>('/api/v1/server-monitor/status');
      setStatus(r.data);
      setError(null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load server status';
      setError(msg);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, []);

  useEffect(() => { void fetchStatus(); }, [fetchStatus]);
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => { void fetchStatus(); }, 30_000);
    return () => clearInterval(t);
  }, [autoRefresh, fetchStatus]);

  const filteredLogs = useMemo(() => {
    if (!status) return [];
    let logs = status.recentLogs;
    if (logFilter !== 'all') logs = logs.filter(l => l.level.toLowerCase() === logFilter);
    if (logSearch.trim()) {
      const q = logSearch.toLowerCase();
      logs = logs.filter(l =>
        l.message.toLowerCase().includes(q) ||
        l.source.toLowerCase().includes(q));
    }
    return logs;
  }, [status, logFilter, logSearch]);

  const downloadLog = useCallback(() => {
    if (!status) return;
    const text = status.recentLogs
      .map(l => `${fmtTime(l.time)}  [${l.level.toUpperCase().padEnd(5)}]  ${l.source.padEnd(12)}  ${l.message}`)
      .join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `server-log-${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [status]);

  if (loading && !status) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--ai-muted)' }}>
      <LoaderSymbol size={32} />
      <div style={{ marginTop: 12 }}>Loading server status…</div>
    </div>;
  }

  if (error && !status) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#b91c1c' }}>
      <i className="ti ti-alert-triangle" style={{ fontSize: 32 }} />
      <div style={{ marginTop: 12 }}>{error}</div>
    </div>;
  }

  if (!status) return null;

  const cpuColor    = status.cpu.usagePercent >= 90 ? '#dc2626' : status.cpu.usagePercent >= 70 ? '#ea580c' : '#1F4E79';
  const memColor    = status.memory.usagePercent >= 80 ? '#ea580c' : status.memory.usagePercent >= 70 ? '#d97706' : '#1F4E79';

  return (
    <div
      className="sm-scroll"
      style={{
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        background: 'var(--ai-bg)',
        fontFamily: 'var(--ai-font)',
      }}
    >
      <style>{`
        /* Polished scrollbar — narrow, subtle, matches BTSoft palette */
        .sm-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
        .sm-scroll::-webkit-scrollbar-track { background: transparent; }
        .sm-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
          border: 2px solid var(--ai-bg);
        }
        .sm-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .sm-scroll { scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent; }

        /* Card hover lift */
        .sm-card {
          transition: box-shadow .15s, transform .15s;
        }
        .sm-card:hover {
          box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
        }
      `}</style>

    <div style={{ padding: '20px 28px', maxWidth: 1600, margin: '0 auto' }}>
      {/* ─── Header ────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: 'var(--ai-muted)' }}>
          <a href="/" style={{ color: 'var(--ai-muted)', textDecoration: 'none' }}>Home</a>
          {' › '}<span>Server Monitor</span>
        </div>
        {/* Shared FormTitle — the ONE page heading (§18.6): nav icon/colour via
            navMeta('servermon') + the TOP load-time chip. */}
        <FormTitle pageId="servermon" loadMs={loadMs} loading={loading} />
        <div style={{ fontSize: 12, color: 'var(--ai-muted)' }}>
          ALPACA LIMS server&nbsp;&nbsp;·&nbsp;&nbsp;
          <span>{status.ipAddress}</span>&nbsp;&nbsp;·&nbsp;&nbsp;
          Last updated: <span style={{ fontWeight: 600 }}>{fmtTimeShort(status.lastUpdated)}</span>
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: '#555', display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <span><i className="ti ti-mail" style={{ marginRight: 4 }} /><b>Warning alerts</b> → {status.alerts.warningEmail}</span>
          <span><i className="ti ti-mail" style={{ marginRight: 4 }} /><b>Critical alerts</b> → {status.alerts.criticalEmail}</span>
          <span style={{ color: 'var(--ai-muted)' }}>|&nbsp;&nbsp;One email per breach · re-sends after recovery</span>
        </div>
      </div>

      {/* ─── Warning banner ────────────────────────────────────────── */}
      {status.warnings.length > 0 && (
        <div style={{
          display: 'flex', gap: 12,
          background: '#fef3c7', border: '1px solid #fbbf24',
          borderLeft: '4px solid #d97706',
          padding: '12px 16px', borderRadius: 4, marginBottom: 16, fontSize: 13,
        }}>
          <i className="ti ti-alert-triangle" style={{ fontSize: 20, color: '#d97706', flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 700, color: '#92400e' }}>WARNING — Performance degradation detected</div>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18, color: '#78350f' }}>
              {status.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        </div>
      )}

      {/* ─── Status row + refresh controls ─────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, fontSize: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            color: status.allSystemsOk ? '#16a34a' : '#d97706', fontWeight: 600,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%',
              background: status.allSystemsOk ? '#16a34a' : '#d97706' }} />
            {status.allSystemsOk ? 'All systems operational' : 'Degraded performance'}
          </span>
          <span style={{ color: 'var(--ai-muted)' }}>Uptime: <b>{status.uptimeDays} days</b></span>
        </div>
        {/* Shared .ca-btn buttons + prototype glyphs (BO UI Standard §7) —
            read-only monitor, so Refresh + the auto-refresh toggle are the
            only actions (no CRUD). */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`sa-filter-btn${autoRefresh ? ' active' : ''}`}
                  onClick={() => setAutoRefresh(!autoRefresh)}>
            <i className={`ti ti-${autoRefresh ? 'player-pause' : 'player-play'}`} />
            {autoRefresh ? ' Auto-refresh (30s)' : ' Auto-refresh OFF'}
          </button>
          <button className="ca-btn" onClick={() => void fetchStatus()}>
            <span className="tb-i tb-i-green">🔄</span> Refresh
          </button>
        </div>
      </div>

      {/* ─── Metric cards ──────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        {/* CPU */}
        <div className="sm-card" style={cardStyle}>
          <div style={cardHdr}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: '#555' }}>CPU</span>
            <i className="ti ti-cpu" style={{ color: 'var(--ai-muted)' }} />
          </div>
          <div style={{ position: 'relative', textAlign: 'center' }}>
            <Donut pct={status.cpu.usagePercent} color={cpuColor} />
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                 fontSize: 22, fontWeight: 700, color: cpuColor }}>
              {Math.round(status.cpu.usagePercent)}%
            </div>
          </div>
          <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--ai-muted)', marginTop: -8 }}>
            Avg {Math.round(status.cpu.usagePercent)}% · {status.cpu.cores} cores
          </div>
        </div>

        {/* Memory */}
        <div className="sm-card" style={cardStyle}>
          <div style={cardHdr}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: '#555' }}>MEMORY (RAM)</span>
            <i className="ti ti-stack-2" style={{ color: 'var(--ai-muted)' }} />
          </div>
          <div style={{ position: 'relative', textAlign: 'center' }}>
            <Donut pct={status.memory.usagePercent} color={memColor} />
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                 fontSize: 22, fontWeight: 700, color: memColor }}>
              {Math.round(status.memory.usagePercent)}%
            </div>
          </div>
          <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--ai-muted)', marginTop: -8 }}>
            {status.memory.usedGb} / {status.memory.totalGb} GB
          </div>
        </div>

        {/* Services */}
        <div className="sm-card" style={cardStyle}>
          <div style={cardHdr}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: '#555' }}>SERVICES</span>
            <i className="ti ti-server" style={{ color: 'var(--ai-muted)' }} />
          </div>
          <div style={{ padding: '4px 0', fontSize: 12 }}>
            {status.services.map(s => (
              <div key={s.name} style={{ display: 'flex', justifyContent: 'space-between',
                   alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span>{s.name}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                  color: s.online ? '#16a34a' : '#dc2626', fontWeight: 600, fontSize: 11 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%',
                    background: s.online ? '#16a34a' : '#dc2626' }} />
                  {s.state}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Network & Uptime */}
        <div className="sm-card" style={cardStyle}>
          <div style={cardHdr}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: '#555' }}>NETWORK &amp; UPTIME</span>
            <i className="ti ti-world" style={{ color: 'var(--ai-muted)' }} />
          </div>
          <div style={{ padding: '4px 0', fontSize: 12 }}>
            {[
              { label: 'Response time', value: `${status.network.responseTimeMs}ms` },
              { label: 'Active sessions', value: status.network.activeSessions },
              { label: 'Requests/min', value: status.network.requestsPerMinute },
              { label: 'Uptime', value: `${status.network.uptimeDays} days` },
            ].map(m => (
              <div key={m.label} style={{ display: 'flex', justifyContent: 'space-between',
                   padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: 'var(--ai-muted)' }}>{m.label}</span>
                <span style={{ fontWeight: 600 }}>{m.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── CPU Breakdown — who's using the CPU? ─────────────────── */}
      <CpuBreakdownCard consumers={status.cpu.topConsumers ?? []}
                        totalPct={status.cpu.usagePercent} />

      {/* ─── Memory Breakdown — who's using the RAM? ──────────────── */}
      <MemoryBreakdownCard consumers={status.memory.topConsumers ?? []}
                           totalGb={status.memory.totalGb}
                           usedGb={status.memory.usedGb} />

      {/* ─── IIS Application Pool Memory — drill into IIS pools ─── */}
      <IisAppPoolsCard pools={status.iisAppPools ?? []}
                       totalGb={status.memory.totalGb} />

      {/* ─── ALPACA XAF Runtime — .NET CLR + ASP.NET deep-dive ──── */}
      <XafRuntimeCard rows={status.xafRuntimes ?? []} />

      {/* ─── Disk Usage ────────────────────────────────────────────── */}
      <div style={{ ...cardStyle, padding: '14px 20px', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
          <i className="ti ti-database" style={{ marginRight: 6 }} /> Disk Usage
        </div>
        {status.disks.map(d => {
          const barColor = d.usagePercent >= 80 ? '#ea580c' : d.usagePercent >= 60 ? '#d97706' : '#1F4E79';
          return (
            <div key={d.letter} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                   fontSize: 12, marginBottom: 4 }}>
                <span>{d.letter}: {d.label}</span>
                <span style={{ color: 'var(--ai-muted)' }}>
                  <b>{d.usedGb} GB</b> / {d.totalGb} GB ({Math.round(d.usagePercent)}%)
                </span>
              </div>
              <div style={{ background: '#e5e7eb', height: 12, borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${d.usagePercent}%`, height: '100%',
                              background: barColor, transition: 'width 0.3s' }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* ─── Recent Log Entries ────────────────────────────────────── */}
      <div style={{ ...cardStyle, padding: '14px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>
            <i className="ti ti-list" style={{ marginRight: 6 }} /> Recent Log Entries
          </div>
          {/* Shared .ca-btn + prototype glyphs — Refresh + the log download
              ("Export to"-style action, existing export logic). */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="ca-btn" onClick={() => void fetchStatus()}>
              <span className="tb-i tb-i-green">🔄</span> Refresh
            </button>
            <button className="ca-btn" onClick={downloadLog}>
              <span className="tb-i tb-i-teal">📤</span> Download log
            </button>
          </div>
        </div>

        {/* filter row — shared .list-search + .sa-filter-btn chips */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
          <div className="list-search" style={{ flex: '0 0 280px' }}>
            <span className="tb-i">🔍</span>
            <input
              type="text"
              placeholder="Filter log entries…"
              value={logSearch}
              onChange={e => setLogSearch(e.target.value)}
            />
            {logSearch && (
              <span className="list-search-clear" onClick={() => setLogSearch('')}>×</span>
            )}
          </div>
          {(['all','info','warn','error'] as LogFilter[]).map(f => (
            <button key={f} onClick={() => setLogFilter(f)}
                    className={`sa-filter-btn${logFilter === f ? ' active' : ''}`}
                    style={{ textTransform: 'capitalize' }}>
              {f}
            </button>
          ))}
        </div>

        {/* log table */}
        {/* Table chrome (header bg / paddings / separators / ink) comes from
            the shared token-based .smon-tbl classes in servermonitor.css. */}
        <table className="smon-tbl">
          <thead>
            <tr>
              <th style={{ width: 160 }}>Time</th>
              <th style={{ width: 70 }}>Level</th>
              <th style={{ width: 140 }}>Source</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: 24, textAlign: 'center', color: '#999' }}>
                No log entries match the current filter.
              </td></tr>
            ) : filteredLogs.map((l, i) => (
              <tr key={i} style={{
                    background: l.level.toLowerCase() === 'warn' ? '#fffbeb'
                              : l.level.toLowerCase() === 'error' ? '#fef2f2'
                              : 'transparent' }}>
                <td style={{ color: '#555', fontFamily: 'monospace' }}>
                  {fmtTime(l.time)}
                </td>
                <td><LevelBadge level={l.level} /></td>
                <td style={{ color: '#374151' }}>{l.source}</td>
                <td>{l.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </div>
  );
}

// ── Shared styles (inline so this page is self-contained) ─────────
// Card skeleton per BO UI Standard: white card, --ai-line border, radius 10,
// the shared card shadow token.
const cardStyle: React.CSSProperties = {
  background: 'var(--ai-card)', border: '1px solid var(--ai-line)', borderRadius: 10,
  padding: '16px 18px',
  boxShadow: 'var(--ai-shadow-card)',
};
const cardHdr: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  marginBottom: 12,
};
// btnStyle REMOVED 2026-07-02 — all page actions use the shared .ca-btn /
// .sa-filter-btn (theme/boStandard.css + backlog.css, BO UI Standard).
